import React, { useState } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Chip,
    Grid,
    Alert,
    CircularProgress,
    Divider,
    Paper,
} from '@mui/material'
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Email as EmailIcon,
    AttachFile as AttachFileIcon,
    Close as CloseIcon,
} from '@mui/icons-material'
import { useSnackbars } from '@assembly/hooks'
import { SnackbarType } from '@assembly/contexts'
import { InvoiceResponse, clientInvoicesApi } from '@assembly/api/clientInvoices'

interface Props {
    open: boolean
    invoice: InvoiceResponse | null
    onClose: () => void
}

interface EmailFormData {
    toAddresses: string[]
    ccAddresses: string[]
    additionalNote: string
    attachment: File | null
}

export default function EmailInvoiceModal({ open, invoice, onClose }: Props) {
    const { addAlert } = useSnackbars()

    const [formData, setFormData] = useState<EmailFormData>({
        toAddresses: [''],
        ccAddresses: [''],
        additionalNote: '',
        attachment: null,
    })

    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)
    const [responseMessage, setResponseMessage] = useState('')
    const [newToEmail, setNewToEmail] = useState('')
    const [newCcEmail, setNewCcEmail] = useState('')

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email.trim())
    }

    const handleAddToEmail = () => {
        if (newToEmail.trim() && validateEmail(newToEmail.trim())) {
            setFormData(prev => ({
                ...prev,
                toAddresses: [...prev.toAddresses.filter(email => email.trim()), newToEmail.trim()]
            }))
            setNewToEmail('')
        } else {
            addAlert({
                message: 'Please enter a valid email address',
                type: SnackbarType.Error,
            })
        }
    }

    const handleAddCcEmail = () => {
        if (newCcEmail.trim() && validateEmail(newCcEmail.trim())) {
            setFormData(prev => ({
                ...prev,
                ccAddresses: [...prev.ccAddresses.filter(email => email.trim()), newCcEmail.trim()]
            }))
            setNewCcEmail('')
        } else {
            addAlert({
                message: 'Please enter a valid email address',
                type: SnackbarType.Error,
            })
        }
    }

    const handleRemoveToEmail = (index: number) => {
        setFormData(prev => ({
            ...prev,
            toAddresses: prev.toAddresses.filter((_, i) => i !== index)
        }))
    }

    const handleRemoveCcEmail = (index: number) => {
        setFormData(prev => ({
            ...prev,
            ccAddresses: prev.ccAddresses.filter((_, i) => i !== index)
        }))
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] || null
        setFormData(prev => ({ ...prev, attachment: file }))
    }

    const handleRemoveFile = () => {
        setFormData(prev => ({ ...prev, attachment: null }))
    }

    const handleSubmit = async () => {
        if (!invoice) return

        // Validate that we have at least one recipient
        const validToEmails = formData.toAddresses.filter(email => email.trim() && validateEmail(email.trim()))
        if (validToEmails.length === 0) {
            addAlert({
                message: 'Please add at least one valid recipient email address',
                type: SnackbarType.Error,
            })
            return
        }

        // Validate CC emails if any
        const validCcEmails = formData.ccAddresses.filter(email => email.trim() && validateEmail(email.trim()))

        try {
            setLoading(true)
            setSuccess(false)
            setResponseMessage('')

            const emailData = {
                toAddresses: validToEmails,
                ccAddresses: validCcEmails.length > 0 ? validCcEmails : undefined,
                additionalNote: formData.additionalNote.trim() || undefined,
                attachment: formData.attachment || undefined,
            }

            const response = await clientInvoicesApi.sendInvoiceEmail(invoice.id, emailData)

            setSuccess(true)
            setResponseMessage(response.data || 'Invoice email sent successfully')
            addAlert({
                message: 'Invoice email sent successfully',
                type: SnackbarType.Success,
            })
        } catch (error: any) {
            console.error('Error sending invoice email:', error)
            const errorMessage = error.response?.data || 'Failed to send invoice email'
            setResponseMessage(errorMessage)
            addAlert({
                message: errorMessage,
                type: SnackbarType.Error,
            })
        } finally {
            setLoading(false)
        }
    }

    const handleClose = () => {
        if (!loading) {
            // Reset form when closing
            setFormData({
                toAddresses: [''],
                ccAddresses: [''],
                additionalNote: '',
                attachment: null,
            })
            setSuccess(false)
            setResponseMessage('')
            setNewToEmail('')
            setNewCcEmail('')
            onClose()
        }
    }

    const handleKeyPress = (event: React.KeyboardEvent, isToEmail: boolean) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            if (isToEmail) {
                handleAddToEmail()
            } else {
                handleAddCcEmail()
            }
        }
    }

    if (!invoice) return null

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1}>
                        <EmailIcon />
                        <Typography variant="h6">
                            Send Invoice Email - {invoice.invoiceNumber}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} disabled={loading}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                        Send invoice {invoice.invoiceNumber} to recipients via email with optional attachment and note.
                    </Typography>
                </Box>

                {success && responseMessage && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {responseMessage}
                    </Alert>
                )}

                {!success && responseMessage && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {responseMessage}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    {/* To Addresses */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>
                            Recipients (To) *
                        </Typography>
                        <Box display="flex" gap={1} mb={2}>
                            <TextField
                                fullWidth
                                label="Add recipient email"
                                value={newToEmail}
                                onChange={(e) => setNewToEmail(e.target.value)}
                                onKeyPress={(e) => handleKeyPress(e, true)}
                                placeholder="recipient@example.com"
                                disabled={loading || success}
                            />
                            <Button
                                variant="outlined"
                                onClick={handleAddToEmail}
                                disabled={loading || success || !newToEmail.trim()}
                                startIcon={<AddIcon />}
                            >
                                Add
                            </Button>
                        </Box>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {formData.toAddresses.filter(email => email.trim()).map((email, index) => (
                                <Chip
                                    key={index}
                                    label={email}
                                    onDelete={() => handleRemoveToEmail(index)}
                                    deleteIcon={<DeleteIcon />}
                                    color="primary"
                                    variant="outlined"
                                    disabled={loading || success}
                                />
                            ))}
                        </Box>
                        {formData.toAddresses.filter(email => email.trim()).length === 0 && (
                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                At least one recipient email is required
                            </Typography>
                        )}
                    </Grid>

                    {/* CC Addresses */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>
                            CC Recipients (Optional)
                        </Typography>
                        <Box display="flex" gap={1} mb={2}>
                            <TextField
                                fullWidth
                                label="Add CC email"
                                value={newCcEmail}
                                onChange={(e) => setNewCcEmail(e.target.value)}
                                onKeyPress={(e) => handleKeyPress(e, false)}
                                placeholder="cc@example.com"
                                disabled={loading || success}
                            />
                            <Button
                                variant="outlined"
                                onClick={handleAddCcEmail}
                                disabled={loading || success || !newCcEmail.trim()}
                                startIcon={<AddIcon />}
                            >
                                Add
                            </Button>
                        </Box>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {formData.ccAddresses.filter(email => email.trim()).map((email, index) => (
                                <Chip
                                    key={index}
                                    label={email}
                                    onDelete={() => handleRemoveCcEmail(index)}
                                    deleteIcon={<DeleteIcon />}
                                    color="secondary"
                                    variant="outlined"
                                    disabled={loading || success}
                                />
                            ))}
                        </Box>
                    </Grid>

                    {/* Additional Note */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>
                            Additional Note (Optional)
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Add a personal note to include in the email"
                            value={formData.additionalNote}
                            onChange={(e) => setFormData(prev => ({ ...prev, additionalNote: e.target.value }))}
                            placeholder="This note will be included in the email body..."
                            disabled={loading || success}
                        />
                    </Grid>

                    {/* File Attachment */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>
                            Attachment (Optional)
                        </Typography>
                        {formData.attachment ? (
                            <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <AttachFileIcon />
                                    <Typography variant="body2">
                                        {formData.attachment.name} ({(formData.attachment.size / 1024 / 1024).toFixed(2)} MB)
                                    </Typography>
                                </Box>
                                <IconButton
                                    onClick={handleRemoveFile}
                                    color="error"
                                    size="small"
                                    disabled={loading || success}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Paper>
                        ) : (
                            <Box>
                                <input
                                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                                    style={{ display: 'none' }}
                                    id="attachment-upload"
                                    type="file"
                                    onChange={handleFileChange}
                                    disabled={loading || success}
                                />
                                <label htmlFor="attachment-upload">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        startIcon={<AttachFileIcon />}
                                        disabled={loading || success}
                                    >
                                        Choose File
                                    </Button>
                                </label>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Supported formats: PDF, DOC, DOCX, TXT, JPG, JPEG, PNG (Max 10MB)
                                </Typography>
                            </Box>
                        )}
                    </Grid>

                    {/* Invoice Summary */}
                    <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            Invoice Summary
                        </Typography>
                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2">
                                        <strong>Invoice #:</strong> {invoice.invoiceNumber}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Organization:</strong> {invoice.organization?.name}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Status:</strong> {invoice.status}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2">
                                        <strong>Amount:</strong> ${invoice.totalAmount?.toFixed(2)}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Due Date:</strong> {new Date(invoice.dueDate).toLocaleDateString()}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Type:</strong> {invoice.type}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    {success ? 'Close' : 'Cancel'}
                </Button>
                {!success && (
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading || formData.toAddresses.filter(email => email.trim()).length === 0}
                        startIcon={loading ? <CircularProgress size={20} /> : <EmailIcon />}
                    >
                        {loading ? 'Sending...' : 'Send Email'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}


